/**
 * Le budget de performance du § 7.3, rendu vérifiable.
 *
 * Le plan fixe des plafonds (triangles, draw calls, corps physiques, DPR, mémoire
 * GPU) mais rien ne les contrôlait : ils vivaient dans un tableau Markdown, et un
 * tableau ne casse pas quand on le dépasse. Ce module les rend exécutables.
 *
 * ⚠️ Ce qu'il ne fait PAS, et qui reste à faire sur appareil réel : le fps sur
 * iPhone 11 et la dérive thermique d'une session de 20 à 30 minutes. Ces deux-là ne
 * se simulent pas — un rendu logiciel sur portable ne dit rien de la chaleur d'un
 * téléphone. Ce qui est ici, en revanche, est vrai partout : la géométrie envoyée au
 * GPU et le nombre d'appels de dessin ne dépendent pas de la machine.
 */

/** Compteurs relevés sur le rendu — miroir de `renderer.info` de three.js. */
export interface Compteurs {
  readonly triangles: number;
  readonly appelsDeDessin: number;
  /** Géométries et textures vivantes : c'est là qu'une fuite se voit. */
  readonly geometries: number;
  readonly textures: number;
  /** Programmes de shader compilés. */
  readonly programmes: number;
  /** Corps physiques simulés pendant le rendu. Doit valoir 0 : le jet est calculé
   *  hors écran et seulement rejoué (§ 7.1). */
  readonly corpsDynamiques: number;
  readonly dpr: number;
}

/** Les plafonds du § 7.3. */
export const BUDGET = {
  triangles: 60_000,
  appelsDeDessin: 12,
  /**
   * Zéro, et non trois comme l'annonçait le plan : depuis que le jet est calculé
   * d'avance puis rejoué, il n'y a plus aucun corps physique dans la boucle de
   * rendu. C'est un budget tenu par construction, pas par vigilance.
   */
  corpsDynamiques: 0,
  dpr: 2,
  /** Une seule ombre, donc peu de programmes : au-delà, un matériau a été dupliqué. */
  programmes: 12,
  geometries: 40,
  textures: 12,
} as const;

export interface Depassement {
  readonly quoi: string;
  readonly mesure: number;
  readonly plafond: number;
}

/** Ce qui dépasse le budget. Vide quand tout tient. */
export function depassements(
  compteurs: Compteurs, budget: typeof BUDGET = BUDGET,
): readonly Depassement[] {
  const controles: ReadonlyArray<readonly [string, number, number]> = [
    ["triangles", compteurs.triangles, budget.triangles],
    ["appels de dessin", compteurs.appelsDeDessin, budget.appelsDeDessin],
    ["corps dynamiques", compteurs.corpsDynamiques, budget.corpsDynamiques],
    ["DPR", compteurs.dpr, budget.dpr],
    ["programmes", compteurs.programmes, budget.programmes],
    ["géométries", compteurs.geometries, budget.geometries],
    ["textures", compteurs.textures, budget.textures],
  ];
  return controles
    .filter(([, mesure, plafond]) => mesure > plafond)
    .map(([quoi, mesure, plafond]) => ({ quoi, mesure, plafond }));
}

export function tientLeBudget(compteurs: Compteurs, budget: typeof BUDGET = BUDGET): boolean {
  return depassements(compteurs, budget).length === 0;
}

/** Résumé lisible, pour la sonde et les rapports de test. */
export function resumer(compteurs: Compteurs): string {
  return [
    `${compteurs.triangles} triangles`,
    `${compteurs.appelsDeDessin} appels`,
    `${compteurs.geometries} géométries`,
    `${compteurs.textures} textures`,
    `${compteurs.programmes} programmes`,
    `DPR ${compteurs.dpr}`,
  ].join(" · ");
}

// ------------------------------------------------------- détection de fuite

/**
 * Vrai si les ressources GPU grandissent au fil des relevés.
 *
 * Une session longue est le seul moyen de voir une fuite : un `dispose()` oublié ne
 * coûte rien au premier lancer et tue le téléphone au centième. On compare le
 * premier relevé au dernier plutôt que de suivre la pente, parce qu'un pic
 * transitoire pendant un lancer est normal — c'est l'accumulation qui ne l'est pas.
 *
 * `tolerance` autorise quelques ressources de plus : three.js compile un programme
 * de shader supplémentaire à la première ombre, par exemple.
 */
export function fuiteDetectee(
  releves: readonly Compteurs[], tolerance = 2,
): { fuite: boolean; details: string } {
  if (releves.length < 2) return { fuite: false, details: "pas assez de relevés" };
  const premier = releves[0]!;
  const dernier = releves[releves.length - 1]!;
  const croissances = (["geometries", "textures", "programmes"] as const)
    .map((quoi) => ({ quoi, delta: dernier[quoi] - premier[quoi] }))
    .filter(({ delta }) => delta > tolerance);
  return {
    fuite: croissances.length > 0,
    details: croissances.length === 0
      ? `stable sur ${releves.length} relevés`
      : croissances.map(({ quoi, delta }) => `${quoi} +${delta}`).join(", "),
  };
}
